// FirstAgentPeekModal — fires the first time a Beginner Mode user
// completes skill #10. Earlier than the AgentUnlockModal (which still
// fires at #25 to mark the Awareness → Application tier transition).
//
// Goal: give the beginner a tangible "I built something real" moment
// in the middle of the Awareness tier, where motivation typically
// dips. We route them into the existing /agents/builder with a
// gentle, beginner-friendly starter goal — no tools required, just a
// persona + system prompt.
//
// One-shot: dismiss flips `firstAgentPeekShown` so we never re-fire.
import { useNavigate } from "react-router-dom";
import { Bot, Sparkles, X, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBeginnerMode } from "@/hooks/useBeginnerMode";

// Intentionally simpler than the #25 starter goal — no web search,
// no email. A 3-field guided template (name, role, system prompt)
// is enough to ship the user's first agent in under a minute.
const PEEK_STARTER_GOAL =
  "Help me brainstorm 3 angles for any topic I throw at you, in plain English.";

const FirstAgentPeekModal = () => {
  const navigate = useNavigate();
  const { justUnlockedFirstAgentPeek, dismissFirstAgentPeek } = useBeginnerMode();

  const handleBuild = () => {
    dismissFirstAgentPeek();
    const params = new URLSearchParams({
      goal: PEEK_STARTER_GOAL,
      // No `tools` param — the peek build is intentionally toolless so
      // the user focuses on persona/prompt, not plumbing.
      template: "beginner",
    });
    navigate(`/agents/builder?${params.toString()}`);
  };

  return (
    <Dialog
      open={justUnlockedFirstAgentPeek}
      onOpenChange={(open) => {
        if (!open) dismissFirstAgentPeek();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-primary/15 grid place-items-center">
            <Bot className="w-7 h-7 text-primary" />
          </div>
          <DialogTitle className="text-center font-mono text-xl">
            <Sparkles className="inline w-4 h-4 text-primary mr-1" />
            10 skills in. Want to build something?
          </DialogTitle>
          <DialogDescription className="text-center font-mono text-xs pt-2">
            You've talked to AI ten different ways. Now flip the script —
            give it a job and a personality, and let it work for you.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 my-2">
          <p className="text-xs font-mono font-bold text-foreground mb-1">
            Build your first mini-agent (60 seconds)
          </p>
          <p className="text-[11px] font-mono text-muted-foreground leading-relaxed">
            Pick a name, a role, and how it should behave. We'll save it
            so you can chat with it any time. No tools, no plumbing —
            just persona + prompt. The deeper "agents-with-tools" unlock
            comes at skill #25.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleBuild}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Bot className="w-4 h-4" />
            Build my first agent
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={dismissFirstAgentPeek}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
            Maybe later — keep learning
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FirstAgentPeekModal;